import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { X, Calendar } from "lucide-react";

type Props = {
  src: string;
  bookHref?: string;
};

export default function FloatingVideoWidget({ src, bookHref = "/book" }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [closed, setClosed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (expanded) {
      v.muted = false;
      v.currentTime = 0;
      void v.play().catch(() => {
        v.muted = true;
        void v.play().catch(() => {});
      });
    } else {
      v.muted = true;
      void v.play().catch(() => {});
    }
  }, [expanded]);

  if (closed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 print:hidden">
      <div
        className={[
          "relative overflow-hidden rounded-2xl border-2 border-primary/70 bg-black shadow-2xl transition-all duration-300",
          expanded
            ? "flex w-[min(20rem,calc(100vw-2rem))] flex-col gap-2 p-2 sm:block sm:w-[min(22rem,calc(100vw-2rem))] sm:p-0"
            : "aspect-[9/16] w-[7.5rem] sm:w-[9rem]",
        ].join(" ")}
      >
        {expanded && (
          <Link
            to={bookHref}
            className="hidden sm:flex shrink-0 w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground shadow-lg hover:opacity-90 sm:mx-2 sm:mt-2 sm:w-auto"
          >
            <Calendar className="h-3.5 w-3.5" />
            Book a Meeting
          </Link>
        )}

        <div
          className={[
            "relative w-full",
            expanded
              ? "aspect-[9/16] overflow-hidden rounded-lg sm:rounded-2xl"
              : "h-full",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <video
            ref={videoRef}
            src={src}
            autoPlay
            muted
            loop={!expanded}
            controls={expanded}
            playsInline
            preload="metadata"
            onClick={() => !expanded && setExpanded(true)}
            className={[
              "h-full w-full cursor-pointer",
              expanded ? "object-contain" : "object-cover",
            ].join(" ")}
          />

          <button
            type="button"
            onClick={() => (expanded ? setExpanded(false) : setClosed(true))}
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-md hover:opacity-90"
            aria-label={expanded ? "Minimize video" : "Close video"}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
